import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ExpertProfile = ({ expert, onClose, onViewEvents, onFollow }) => {
  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={16} className="text-accent fill-current" />
      );
    }
    
    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="Star" size={16} className="text-accent fill-current opacity-50" />
      );
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={16} className="text-muted-foreground" />
      );
    }
    
    return stars;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-wellness-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl text-foreground">
            Expert Profile
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Expert Info */}
        <div className="p-6">
          <div className="flex items-start space-x-6 mb-6">
            {/* Profile Image */}
            <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
              <Image
                src={expert?.profileImage}
                alt={expert?.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                {expert?.name}
              </h3>
              <p className="text-muted-foreground mb-3">{expert?.credentials}</p>
              
              {/* Rating */}
              <div className="flex items-center space-x-2 mb-3">
                <div className="flex items-center space-x-1">
                  {renderRating(expert?.rating)}
                </div>
                <span className="text-sm text-muted-foreground">
                  {expert?.rating} ({expert?.reviewCount} reviews)
                </span>
              </div>

              {/* Stats */}
              <div className="flex items-center space-x-6 mb-4">
                <div className="text-center">
                  <div className="font-semibold text-foreground">{expert?.totalEvents}</div>
                  <div className="text-xs text-muted-foreground">Events</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-foreground">{expert?.totalAttendees}</div>
                  <div className="text-xs text-muted-foreground">Attendees</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-foreground">{expert?.yearsExperience}</div>
                  <div className="text-xs text-muted-foreground">Years Exp.</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  variant="default"
                  onClick={() => onViewEvents(expert)}
                  iconName="Calendar"
                  iconPosition="left"
                >
                  View Events
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onFollow(expert)}
                  iconName={expert?.isFollowing ? "UserCheck" : "UserPlus"}
                  iconPosition="left"
                >
                  {expert?.isFollowing ? 'Following' : 'Follow'}
                </Button>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="mb-6">
            <h4 className="font-heading font-medium text-foreground mb-3">About</h4>
            <p className="text-muted-foreground leading-relaxed">
              {expert?.bio}
            </p>
          </div>

          {/* Specializations */}
          <div className="mb-6">
            <h4 className="font-heading font-medium text-foreground mb-3">Specializations</h4>
            <div className="flex flex-wrap gap-2">
              {expert?.specializations?.map((spec, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                >
                  {spec}
                </span>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="mb-6">
            <h4 className="font-heading font-medium text-foreground mb-3">Certifications</h4>
            <div className="space-y-2">
              {expert?.certifications?.map((cert, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <Icon name="Award" size={16} className="text-accent" />
                  <span className="text-sm text-foreground">{cert}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Reviews */}
          <div className="mb-6">
            <h4 className="font-heading font-medium text-foreground mb-3">Recent Reviews</h4>
            <div className="space-y-4">
              {expert?.recentReviews?.map((review, index) => (
                <div key={index} className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{review?.memberName}</span>
                      <div className="flex items-center space-x-1">
                        {renderRating(review?.rating)}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{review?.date}</span>
                  </div>
                  <p className="text-sm text-muted-foreground italic">
                    "{review?.comment}"
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Event: {review?.eventTitle}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="font-heading font-medium text-foreground mb-3">Contact Information</h4>
            <div className="space-y-2">
              {expert?.email && (
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{expert?.email}</span>
                </div>
              )}
              {expert?.website && (
                <div className="flex items-center space-x-3">
                  <Icon name="Globe" size={16} className="text-muted-foreground" />
                  <a 
                    href={expert?.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {expert?.website}
                  </a>
                </div>
              )}
              {expert?.socialMedia && (
                <div className="flex items-center space-x-3">
                  <Icon name="Share2" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">@{expert?.socialMedia}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertProfile;